const EMPTY_VALUE = 'N/A';

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const parseStructuredString = (value) => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;

  try {
    const parsed = JSON.parse(trimmed);
    return isPlainObject(parsed) || Array.isArray(parsed) ? parsed : value;
  } catch {
    return value;
  }
};

const formatLabel = (key) => String(key)
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const PrimitiveValue = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="structured-data-placeholder">{EMPTY_VALUE}</span>;
  }

  if (typeof value === 'boolean') return <span>{value ? 'true' : 'false'}</span>;
  return <span className="structured-data-primitive">{String(value)}</span>;
};

const StructuredValue = ({ value, nested = false }) => {
  const normalizedValue = typeof value === 'string' ? parseStructuredString(value) : value;

  if (Array.isArray(normalizedValue)) {
    if (normalizedValue.length === 0) {
      return <div className="structured-data-empty">No data</div>;
    }

    if (normalizedValue.every(isPlainObject)) {
      const columns = Array.from(new Set(normalizedValue.flatMap((record) => Object.keys(record))));

      if (columns.length === 0) {
        return <div className="structured-data-empty">No fields</div>;
      }

      return (
        <div className={`structured-data-table-scroll${nested ? ' structured-data-nested' : ''}`}>
          <table className="structured-data-table">
            <thead>
              <tr>
                {columns.map((column) => <th key={column} scope="col">{formatLabel(column)}</th>)}
              </tr>
            </thead>
            <tbody>
              {normalizedValue.map((record, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column) => (
                    <td key={column}>
                      {Object.prototype.hasOwnProperty.call(record, column)
                        ? <StructuredValue value={record[column]} nested />
                        : <span className="structured-data-placeholder">{EMPTY_VALUE}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <ul className={`structured-data-list${nested ? ' structured-data-nested' : ''}`}>
        {normalizedValue.map((item, index) => (
          <li key={index}><StructuredValue value={item} nested /></li>
        ))}
      </ul>
    );
  }

  if (isPlainObject(normalizedValue)) {
    const entries = Object.entries(normalizedValue);
    if (entries.length === 0) {
      return <div className="structured-data-empty">No fields</div>;
    }

    return (
      <div className={`structured-data-table-scroll${nested ? ' structured-data-nested' : ''}`}>
        <table className="structured-data-table structured-data-object-table">
          <tbody>
            {entries.map(([key, entryValue]) => (
              <tr key={key}>
                <th scope="row">{formatLabel(key)}</th>
                <td><StructuredValue value={entryValue} nested /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <PrimitiveValue value={normalizedValue} />;
};

const StructuredDataView = ({ data, emptyMessage = 'No data' }) => {
  if (data === null || data === undefined || (Array.isArray(data) && data.length === 0)) {
    return <div className="structured-data-empty">{emptyMessage}</div>;
  }

  return (
    <div className="structured-data-view">
      <StructuredValue value={data} />
    </div>
  );
};

export default StructuredDataView;